import React from "react";
import { X, FilePlus, History } from "lucide-react";
import { formatDate } from "./dpi";

/**
 * Start-mode picker shown before opening the verifica form when prior
 * revisions exist for a scaffold. Lets the revisore choose between starting
 * a brand-new (empty) verifica or cloning the most recent one as a baseline.
 *
 * Cloning copies the structure of the previous inspection (damage rows with
 * colours/positions/notes, criticità, conclusioni, note generali) so the
 * revisore can edit it in-place rather than re-typing everything.
 */
export default function RevisionStartPicker({ open, lastRevision, onClose, onPickBlank, onPickClone }) {
  if (!open) return null;
  const damageCount = (lastRevision?.damage_entries || []).length;
  const last = lastRevision?.revision_date ? formatDate(lastRevision.revision_date) : "—";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      data-testid="revision-start-picker"
      onClick={onClose}
    >
      <div
        className="bru-card w-full max-w-2xl p-6 mt-12"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="tag-label">NUOVA VERIFICA</div>
            <h3 className="text-2xl font-bold">Come vuoi iniziare?</h3>
            <p className="text-xs text-neutral-500 mt-1">
              È già presente almeno una verifica per questa scaffalatura. Puoi
              partire da zero oppure clonare l&apos;ultima verifica salvata.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="bru-btn bru-btn-ghost !px-2 !py-1"
            data-testid="close-start-picker"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={onPickBlank}
            className="text-left p-5 border-[1.5px] border-black bg-white hover:bg-[#FFF6EE] transition group"
            data-testid="start-mode-blank"
          >
            <div className="flex items-center gap-2 mb-2">
              <FilePlus className="w-5 h-5" />
              <div className="font-bold">Nuova verifica da zero</div>
            </div>
            <p className="text-sm text-neutral-600 leading-snug">
              Apre il modulo completamente vuoto. Comportamento standard.
            </p>
          </button>

          <button
            type="button"
            onClick={onPickClone}
            disabled={!lastRevision}
            className="text-left p-5 border-[1.5px] border-black bg-[#0A0A0A] text-white hover:bg-[#FF5A00] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition group"
            data-testid="start-mode-clone"
          >
            <div className="flex items-center gap-2 mb-2">
              <History className="w-5 h-5" />
              <div className="font-bold">Usa verifica precedente</div>
            </div>
            <p className="text-sm leading-snug opacity-90">
              Pre-compila la nuova verifica con i dati dell&apos;ultima salvata
              ({last} · {damageCount} righe danni), incluse le foto della
              tabella. La firma viene resettata. Potrai poi
              modificare/eliminare/aggiungere direttamente le righe.
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
